import { createSelector } from "@reduxjs/toolkit";
import { type IBreeamCriterionCheckOutput } from "@mcapps/shared";
import { azureBlobStorageRTK } from "../../../../../azureBlobStorageRTK";
import {
  selectCurrentOpenIssueBoardInModal,
  selectCurrentOpenIssueInModal,
  selectCurrentOpenIssueModal,
} from "../../../issueSelectors";
import { IRootState } from "../../../../../../store";

export type TAiAssessment = {
  verdict: "PASS" | "PARTIAL" | "FAIL" | "UNCERTAIN" | null;
  summary: string;
  breText: string;
  supportingPoints: string[];
  gaps: string[];
};

const verdictColorMap: Record<IBreeamCriterionCheckOutput["verdict"], string> = {
  PASS: "green",
  PARTIAL: "yellow",
  FAIL: "red",
  UNCERTAIN: "gray",
};

export const parseAiAssessment = (rawText?: string): TAiAssessment => {
  if (!rawText?.trim()) {
    return {
      verdict: null,
      summary: "",
      breText: "",
      supportingPoints: [],
      gaps: [],
    };
  }

  const text = rawText.replace(/\r\n/g, "\n").trim();

  const verdictMatch = text.match(/Verdict:\s*(PASS|PARTIAL|FAIL|UNCERTAIN)/i);
  const verdict = (verdictMatch?.[1]?.toUpperCase() as TAiAssessment["verdict"]) ?? null;

  const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const extractSection = (sectionNames: string[], nextSectionNames: string[]) => {
    const sectionPattern = sectionNames.map(escapeRegex).join("|");
    const nextPattern =
      nextSectionNames.length > 0 ? `(?=\\n(?:${nextSectionNames.map(escapeRegex).join("|")}):)` : "$";
    const regex = new RegExp(`(?:${sectionPattern}):\\s*\\n?([\\s\\S]*?)${nextPattern}`, "i");
    const match = text.match(regex);
    return match?.[1]?.trim() ?? "";
  };

  const parseBulletList = (input: string) =>
    input
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => line.replace(/^[-•]+\s*/, "").trim())
      .filter(Boolean);

  const summarySectionNames = ["Kort vurdering", "Summary"];
  const breTextSectionNames = ["Forslag til tekst til BRE", "BRE text", "Tekst til BRE"];
  const supportingSectionNames = ["Dokumentasjon brukt", "Supporting points"];
  const gapsSectionNames = ["Mangler / usikkerheter", "Mangler", "Gaps"];
  const allSectionNames = [
    "Verdict",
    ...summarySectionNames,
    ...breTextSectionNames,
    ...supportingSectionNames,
    ...gapsSectionNames,
  ];

  return {
    verdict,
    summary: extractSection(
      summarySectionNames,
      allSectionNames.filter((sectionName) => !summarySectionNames.includes(sectionName)),
    ),
    breText: extractSection(
      breTextSectionNames,
      allSectionNames.filter((sectionName) => !breTextSectionNames.includes(sectionName)),
    ),
    supportingPoints: parseBulletList(
      extractSection(
        supportingSectionNames,
        allSectionNames.filter((sectionName) => !supportingSectionNames.includes(sectionName)),
      ),
    ),
    gaps: parseBulletList(
      extractSection(
        gapsSectionNames,
        allSectionNames.filter((sectionName) => !gapsSectionNames.includes(sectionName)),
      ),
    ),
  };
};

const EMPTY_ISSUE_FILES_QUERY = {
  data: [],
  isUninitialized: true,
  isLoading: false,
};

export const selectBreeamIssueFilesPath = (projectId: string, issueId: string) => `${projectId}/issue/${issueId}/files`;

export const selectBreeamIssueFilesQueryArgs = createSelector([selectCurrentOpenIssueModal], (modal) => {
  if (modal?.projectId == null || modal.itemId == null) return null;

  return {
    projectId: modal.projectId,
    path: selectBreeamIssueFilesPath(modal.projectId, modal.itemId),
  };
});

export const selectBreeamRunArgs = createSelector([selectCurrentOpenIssueModal], (modal) => {
  if (modal?.projectId == null || modal.itemId == null) return null;

  return {
    projectId: modal.projectId,
    issueId: modal.itemId,
    body: {
      body: {
        runAi: false,
        outputLanguage: "NO",
      },
    },
  };
});

export const selectBreeamModalHeader = createSelector(
  [selectCurrentOpenIssueInModal, selectCurrentOpenIssueBoardInModal],
  (issue, issueBoard) => ({
    issueId: issue?.id ?? "",
    issueTitle: issue?.title ?? "",
    inputAttributeLabel: issueBoard?.otherAttributesConfig?.I?.label ?? "Tekst til BRE online tool",
  }),
);

export const selectBreeamIssueFilesQuery = (state: IRootState) => {
  const queryArgs = selectBreeamIssueFilesQueryArgs(state);

  if (queryArgs == null) {
    return EMPTY_ISSUE_FILES_QUERY;
  }

  return azureBlobStorageRTK.endpoints.getFilesFromAzureBlobStorage.select(queryArgs)(state);
};

const selectIssuePdfFiles = createSelector([selectBreeamIssueFilesQuery], (issueFilesQuery) =>
  (issueFilesQuery.data ?? []).filter((file) => file.fileName?.toLowerCase().endsWith(".pdf")),
);

const selectBreeamGuidanceText = createSelector([selectCurrentOpenIssueInModal], (issue) => {
  const rawGuidanceText = issue?.otherAttributesData?.I;
  const otherAttributeIText =
    typeof rawGuidanceText === "string"
      ? rawGuidanceText
      : rawGuidanceText == null
      ? ""
      : String(rawGuidanceText);
  const descriptionText = typeof issue?.description === "string" ? issue.description : "";

  return otherAttributeIText.trim().length > 0 ? otherAttributeIText : descriptionText;
});

export const selectBreeamGuidanceTextSource = createSelector(
  [selectCurrentOpenIssueInModal, selectBreeamModalHeader],
  (issue, header) => {
    const rawGuidanceText = issue?.otherAttributesData?.I;
    const otherAttributeIText =
      typeof rawGuidanceText === "string"
        ? rawGuidanceText
        : rawGuidanceText == null
        ? ""
        : String(rawGuidanceText);

    return otherAttributeIText.trim().length > 0
      ? {
          title: header.inputAttributeLabel,
          description: "Verdien fra other attribute med id `I`",
        }
      : {
          title: "Beskrivelse",
          description: `Attributt I er tomt. BREEAM-sjekken bruker sakens beskrivelse som veiledningstekst.`,
        };
  },
);

const selectIsCheckingPrerequisites = createSelector(
  [selectBreeamIssueFilesQuery],
  (issueFilesQuery) => issueFilesQuery.isUninitialized === true || issueFilesQuery.isLoading === true,
);

const selectMissingPrerequisites = createSelector(
  [selectBreeamGuidanceText, selectBreeamIssueFilesQuery, selectIssuePdfFiles],
  (guidanceText, issueFilesQuery, issuePdfFiles) => {
    const missing: string[] = [];

    if (guidanceText.trim().length === 0) {
      missing.push("Veiledningstekst fra manualen eller beskrivelse mangler.");
    }

    if (issueFilesQuery.isUninitialized === false && issueFilesQuery.isLoading === false && issuePdfFiles.length === 0) {
      missing.push("Legg til minst én PDF-fil på saken før BREEAM-sjekken kjøres.");
    }

    return missing;
  },
);

export const selectBreeamPrerequisites = createSelector(
  [selectMissingPrerequisites, selectIsCheckingPrerequisites],
  (missingPrerequisites, isCheckingPrerequisites) => ({
    missingPrerequisites,
    isCheckingPrerequisites,
    canRunCheck: missingPrerequisites.length === 0 && isCheckingPrerequisites === false,
  }),
);

export const selectParsedAiAssessment = (result: IBreeamCriterionCheckOutput | null) =>
  parseAiAssessment(result?.analysis?.[0]);

export const selectEffectiveVerdict = (args: {
  parsedAiAssessment: TAiAssessment;
  result: IBreeamCriterionCheckOutput | null;
}): IBreeamCriterionCheckOutput["verdict"] =>
  args.parsedAiAssessment.verdict ?? args.result?.verdict ?? "UNCERTAIN";

export const selectVerdictColor = (verdict: IBreeamCriterionCheckOutput["verdict"]) =>
  verdictColorMap[verdict];

export const selectTechnicalAnalysis = (result: IBreeamCriterionCheckOutput | null) =>
  result?.analysis.slice(1) ?? [];

export const selectCopyableAiAssessmentText = (result: IBreeamCriterionCheckOutput | null) => {
  const parsed = parseAiAssessment(result?.analysis?.[0]);

  return parsed.breText || result?.analysis[0] || "";
};
